import React, { useEffect, useState } from 'react';
import Modal from '../../../../ui/Model';
import Button, { ButtonType } from '../../../../shared/components/core-ui/button/button.component';
import { TicketOwnerStepModel } from '../../../../shared/models/ticket.model';
import { handoffChoices } from '../../../../shared/services/helpers/ticket-rules.helper';

interface Props {
  show: boolean;
  next?: TicketOwnerStepModel;
  saving: boolean;
  onClose: () => void;
  onSubmit: (payload: { team?: string; reason?: string }) => void;
}

/**
 * Hand the ticket to the next team in its category's chain. The reason becomes
 * part of the handoff note, which is the first thing the next team reads.
 */
export const HandoffModal: React.FC<Props> = ({ show, next, saving, onClose, onSubmit }) => {
  const choices = handoffChoices(next);
  const [team, setTeam] = useState<string | undefined>();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!show) return;
    setTeam(choices.mustChoose ? undefined : choices.teams[0]);
    setReason('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  return (
    <Modal
      isOpen={show}
      onClose={onClose}
      containerClassName="lg:h-fit lg:w-[560px]"
      headerContent={
        <div className="text-center font-gilroy-bold text-2xl leading-[100%] text-neutral-500">
          Hand off
        </div>
      }
      footerContent={
        <div className="flex items-center justify-end gap-4">
          <Button type={ButtonType.gray} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type={ButtonType.primary}
            disabled={!team || saving}
            loading={saving}
            onClick={() =>
              onSubmit({
                team: choices.mustChoose ? team : undefined,
                reason: reason.trim() || undefined
              })
            }
          >
            Hand off{team ? ` to ${team}` : ''}
          </Button>
        </div>
      }
    >
      <div className="flex w-full flex-col gap-4">
        {choices.optional ? (
          <p className="text-sm text-neutral-400">
            This step is only needed {next?.when}. If it is not needed, close the ticket instead.
          </p>
        ) : null}
        {choices.mustChoose ? (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-gilroy-medium text-neutral-400">Which team?</span>
            {choices.teams.map((option: string) => (
              <label key={option} className="flex items-center gap-2 text-sm text-neutral-500">
                <input
                  type="radio"
                  name="handoff-team"
                  checked={team === option}
                  onChange={() => setTeam(option)}
                />
                {option}
              </label>
            ))}
          </div>
        ) : null}
        <div>
          <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
            What the next team needs to know
          </label>
          <textarea
            className="min-h-[100px] w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            maxLength={2000}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
        <p className="text-xs text-neutral-300">
          After the handoff the ticket leaves your queue. You may no longer be able to open it.
        </p>
      </div>
    </Modal>
  );
};
